import { useMemo, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, Input, Segmented, Select, TextArea, useLayout, useToast } from "@agentsaas/ui";
import type { Agent, Task } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useAgents } from "../data/queries";
import { putTask, useTasks } from "../data/collab";
import { Loading } from "../layout/Loading";
import { AgentStatusDot } from "../agent/TeamList";
import { CollabFrame } from "../project/ProjectChrome";
import { WorkSwitch } from "../project/WorkSwitch";
import { BOARD_COLUMNS, TASK_TONE, buildTree, groupByColumn, progressOf, type BoardColumn, type TreeNode } from "./model";

type View = "board" | "tree";

/** /p/:pid/tasks：看板（按状态分列）/ 任务树；手机上看板改为按状态分段的列表 */
export function TasksPage() {
  const { pid = "" } = useParams();
  return (
    <CollabFrame pid={pid} section="tasks">
      <TasksView pid={pid} />
    </CollabFrame>
  );
}

function TasksView({ pid }: { pid: string }) {
  const { t } = useT();
  const mobile = useLayout() === "mobile";
  const [sp, setSp] = useSearchParams();
  const view: View = sp.get("view") === "tree" ? "tree" : "board";
  const tasks = useTasks(pid);
  const agents = useAgents(pid);
  const [creating, setCreating] = useState(false);
  const setView = (v: View) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (v === "tree") n.set("view", "tree");
        else n.delete("view");
        return n;
      },
      { replace: true },
    );
  const items = tasks.data ?? [];
  const nameOf = (aid: string | null) => agents.data?.find((a) => a.id === aid);

  return (
    <div className={`tasks${mobile ? " tasks--mobile" : ""}`}>
      {mobile ? <WorkSwitch pid={pid} active="tasks" /> : null}
      <div className="tasks__head">
        {mobile ? null : <h1 className="page__title">{t("task.title")}</h1>}
        <Segmented<View>
          label={t("task.view")}
          value={view}
          onChange={setView}
          options={[
            { value: "board", label: t("task.view.board"), icon: "board" },
            { value: "tree", label: t("task.view.tree"), icon: "tree" },
          ]}
        />
        <span className="grow" />
        <Button variant="primary" icon="plus" onClick={() => setCreating(true)} data-testid="task-new">
          {t("task.new")}
        </Button>
      </div>
      {tasks.isPending ? (
        <Loading />
      ) : !items.length ? (
        <EmptyState icon="board" title={t("task.empty")} />
      ) : view === "tree" ? (
        <TaskTree pid={pid} tasks={items} agentOf={nameOf} />
      ) : mobile ? (
        <StatusSections pid={pid} tasks={items} agentOf={nameOf} />
      ) : (
        <Board pid={pid} tasks={items} agentOf={nameOf} />
      )}
      <NewTaskDialog pid={pid} open={creating} onClose={() => setCreating(false)} tasks={items} agents={agents.data ?? []} />
    </div>
  );
}

type AgentOf = (aid: string | null) => Agent | undefined;

function Board({ pid, tasks, agentOf }: { pid: string; tasks: Task[]; agentOf: AgentOf }) {
  const { t } = useT();
  const cols = useMemo(() => groupByColumn(tasks), [tasks]);
  return (
    <div className="board" data-testid="task-board">
      {BOARD_COLUMNS.map((c) => (
        <section key={c.id} className="board__col" aria-labelledby={`col-${c.id}`} data-testid={`col-${c.id}`}>
          <h2 id={`col-${c.id}`} className="board__head">
            <span className="grow">{t(`task.col.${c.id}`)}</span>
            <Badge>{cols[c.id].length}</Badge>
          </h2>
          <ul className="board__list">
            {cols[c.id].map((x) => (
              <li key={x.id}>
                <TaskCard pid={pid} task={x} agent={agentOf(x.assignee)} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** 手机：选一个状态分段，下面是该状态的任务列表 */
function StatusSections({ pid, tasks, agentOf }: { pid: string; tasks: Task[]; agentOf: AgentOf }) {
  const { t } = useT();
  const cols = useMemo(() => groupByColumn(tasks), [tasks]);
  const [sp, setSp] = useSearchParams();
  const fallback = BOARD_COLUMNS.find((c) => cols[c.id].length)?.id ?? "todo";
  const col = (BOARD_COLUMNS.some((c) => c.id === sp.get("col")) ? sp.get("col") : fallback) as BoardColumn;
  return (
    <div className="sections" data-testid="task-sections">
      <div className="sections__tabs" role="tablist" aria-label={t("task.view.board")}>
        {BOARD_COLUMNS.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={col === c.id}
            className={`chip-tab${col === c.id ? " is-active" : ""}`}
            onClick={() =>
              setSp(
                (cur) => {
                  const n = new URLSearchParams(cur);
                  n.set("col", c.id);
                  return n;
                },
                { replace: true },
              )
            }
            data-testid={`section-${c.id}`}
          >
            {t(`task.col.${c.id}`)}
            <span className="chip-tab__count">{cols[c.id].length}</span>
          </button>
        ))}
      </div>
      {cols[col].length ? (
        <ul className="sections__list" role="tabpanel">
          {cols[col].map((x) => (
            <li key={x.id}>
              <TaskCard pid={pid} task={x} agent={agentOf(x.assignee)} />
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState title={t("task.col_empty")} />
      )}
    </div>
  );
}

export function TaskStatusBadge({ task }: { task: Task }) {
  const { t } = useT();
  return <Badge tone={TASK_TONE[task.status]}>{t(`task.status.${task.status}`)}</Badge>;
}

/** 已完成任务的验收状态 */
export function AcceptBadge({ task }: { task: Task }) {
  const { t } = useT();
  if (task.status !== "completed") return null;
  return task.confirmedAt ? <Badge tone="success">{t("task.accepted")}</Badge> : <Badge tone="warning">{t("task.awaiting_acceptance")}</Badge>;
}

function TaskCard({ pid, task, agent }: { pid: string; task: Task; agent: Agent | undefined }) {
  const { t } = useT();
  return (
    <Link to={`/p/${pid}/tasks/${task.id}`} className="task-card" data-testid="task-card" data-status={task.status}>
      <span className="task-card__title">{task.title}</span>
      <span className="task-card__meta">
        <TaskStatusBadge task={task} />
        <AcceptBadge task={task} />
        {task.prNumber ? (
          <Badge tone="accent">
            <Icon name="pr" size={12} />
            {t("pr.number", { n: task.prNumber })}
          </Badge>
        ) : null}
      </span>
      <span className="task-card__who small muted">
        {agent ? (
          <>
            <AgentStatusDot status={agent.status} />
            {agent.name}
          </>
        ) : (
          t("task.unassigned")
        )}
      </span>
    </Link>
  );
}

function TaskTree({ pid, tasks, agentOf }: { pid: string; tasks: Task[]; agentOf: AgentOf }) {
  const { t } = useT();
  const roots = useMemo(() => buildTree(tasks), [tasks]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const toggle = (id: string) =>
    setCollapsed((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const row = (n: TreeNode, depth: number) => {
    const open = !collapsed.has(n.task.id);
    const p = progressOf(n);
    const agent = agentOf(n.task.assignee);
    return (
      <li key={n.task.id} className="ttree__node" role="treeitem" aria-expanded={n.children.length ? open : undefined} aria-level={depth + 1} data-testid="tree-node">
        <div className="ttree__row" style={{ paddingLeft: 8 + depth * 20 }}>
          {n.children.length ? (
            <button type="button" className="ttree__toggle" aria-label={open ? t("task.collapse") : t("task.expand")} onClick={() => toggle(n.task.id)}>
              <Icon name={open ? "chevronDown" : "chevronRight"} size={16} />
            </button>
          ) : (
            <span className="ttree__toggle" aria-hidden="true" />
          )}
          <Link to={`/p/${pid}/tasks/${n.task.id}`} className="ttree__title ellipsis">
            {n.task.title}
          </Link>
          {p.total ? <span className="muted small">{t("task.progress", { done: p.done, total: p.total })}</span> : null}
          <TaskStatusBadge task={n.task} />
          <span className="ttree__who small muted ellipsis">{agent ? agent.name : t("task.unassigned")}</span>
        </div>
        {n.children.length && open ? <ul role="group">{n.children.map((c) => row(c, depth + 1))}</ul> : null}
      </li>
    );
  };
  return (
    <ul className="ttree" role="tree" aria-label={t("task.view.tree")} data-testid="task-tree">
      {roots.map((r) => row(r, 0))}
    </ul>
  );
}

/** 新建任务：标题、说明、父任务、执行者 */
export function NewTaskDialog({ pid, open, onClose, tasks, agents, parentId }: { pid: string; open: boolean; onClose(): void; tasks: Task[]; agents: Agent[]; parentId?: string }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [spec, setSpec] = useState("");
  const [parent, setParent] = useState(parentId ?? "");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const task = await api.call("taskCreate", {
        params: { pid },
        body: { title: title.trim(), spec, ...(parent ? { parentId: parent } : {}), ...(assignee ? { assignee } : {}) },
      });
      putTask(qc, task);
      toast.show({ message: t("task.created", { title: task.title }), tone: "success", duration: 2500 });
      setTitle("");
      setSpec("");
      setAssignee("");
      onClose();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("task.new")}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={!title.trim()} onClick={() => void submit()} data-testid="task-submit">
            {t("common.create")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit}>
        <Field label={t("task.field.title")} error={error ?? undefined}>
          {(id) => <Input id={id} data-autofocus value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="task-title" />}
        </Field>
        <Field label={t("task.field.spec")} hint={t("task.field.spec_hint")}>
          {(id, d) => <TextArea id={id} aria-describedby={d} rows={5} value={spec} onChange={(e) => setSpec(e.target.value)} data-testid="task-spec" />}
        </Field>
        <Field label={t("task.field.assignee")}>
          {(id) => (
            <Select id={id} value={assignee} onChange={(e) => setAssignee(e.target.value)} data-testid="task-assignee-new">
              <option value="">{t("task.unassigned")}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("task.field.parent")}>
          {(id) => (
            <Select id={id} value={parent} onChange={(e) => setParent(e.target.value)} data-testid="task-parent">
              <option value="">{t("task.no_parent")}</option>
              {tasks.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.title}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </form>
    </Dialog>
  );
}

